export type IntegrationCode = 'atlas' | 'gsl' | 'planisware'

export interface IntegrationDefinition {
    code: IntegrationCode
    label: string
    chipColor: string
    isExternal: boolean
    alwaysEnabled: boolean
}

export const INTEGRATIONS: Record<IntegrationCode, IntegrationDefinition> = {
    atlas: {
        code: 'atlas',
        label: 'Atlas',
        chipColor: 'figma.accentColors.magenta',
        isExternal: false,
        alwaysEnabled: true,
    },
    gsl: {
        code: 'gsl',
        label: 'GSL',
        chipColor: 'figma.accentColors.teal',
        isExternal: true,
        alwaysEnabled: false,
    },
    planisware: {
        code: 'planisware',
        label: 'Planisware',
        chipColor: 'figma.accentColors.green',
        isExternal: true,
        alwaysEnabled: false,
    },
}

export const INTEGRATION_CODES = Object.keys(INTEGRATIONS) as IntegrationCode[]

// Shared chip styling for a source/integration. Use for the filled state on
// both the left-side filter chips and the right-side result-card chips so they
// look identical. Pass the accent color (registry chipColor or
// activity.chipColor). Returns full-saturation background with white-bold
// label, readable on green / teal / magenta.
export const SOURCE_CHIP_SX = (chipColor: string | undefined) => ({
    borderRadius: '12px',
    backgroundColor: chipColor,
    color: '#FFFFFF',
    fontWeight: 600,
    border: 'none',
    '& .MuiChip-label': { color: '#FFFFFF', opacity: 1 },
    '&:hover': { backgroundColor: chipColor },
})

export interface OrganizationIntegrationsMap {
    [code: string]: { enabled?: boolean; config?: unknown } | undefined
}

export const isIntegrationEnabled = (
    integrations: OrganizationIntegrationsMap | undefined,
    code: IntegrationCode
): boolean => {
    const def = INTEGRATIONS[code]
    if (!def) return false
    if (def.alwaysEnabled) return true
    return Boolean(integrations?.[code]?.enabled)
}

// Maps a lowercase integration code to the canonical source string stored on
// activity documents (server-side casing is mixed). Keeps the existing API
// surface and stored-source comparisons unchanged.
export const integrationCodeToSourceString = (code: IntegrationCode): string => {
    switch (code) {
        case 'atlas':
            return 'Atlas'
        case 'gsl':
            return 'GSL'
        case 'planisware':
            return 'Planisware'
    }
}

export const sourceStringToIntegrationCode = (source: string | undefined | null): IntegrationCode | null => {
    if (!source) return null
    const lower = source.toLowerCase()
    return (INTEGRATION_CODES as string[]).includes(lower) ? (lower as IntegrationCode) : null
}
